import React from "react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/react";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { TextField } from "../components/fields/TextField/TextField";
import { MemberLookupMethod } from "../enums/MemberLookupMethod";
import { ChevronUpIcon } from "@chakra-ui/icons";

interface EMLConfigurationDetailFormValues {
  scanSettings: {
    memberFieldLookupOrder: CherryHubAdminApi.MemberLookupMethod[];
    validateUsingRegex: boolean;
    scanDataRegex: string | null;
    scanDataRegexKeepsMatches: boolean;
    allowAlphaCharacters: boolean;
    allowDigits: boolean;
    allowNonAlphanumericCharacters: boolean;
  };
}

interface EMLConfigurationDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  configuration?: CherryHubAdminApi.EventsModuleConfiguration;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: (
    configuration?: CherryHubAdminApi.EventsModuleConfiguration
  ) => void;
  onFailure?: (message?: string) => void;
}

// Create options using all enum values from MemberLookupMethod
const memberLookupMethodOptions = Object.keys(MemberLookupMethod).map(
  (key) => ({
    label: key,
    value: key,
  })
);

const InnerForm = ({
  isSubmitting,
  submitForm,
  isValid,
  onCancel,
  values,
  businessId,
}: EMLConfigurationDetailFormProps &
  FormikProps<EMLConfigurationDetailFormValues>) => {
  return (
    <Box width="100%" alignSelf="flex-start">
      <FormStack>
        <HStack w="100%" mb={4} justifyContent={"flex-end"}>
          <Box>
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
          </Box>

          <Box>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </Box>
        </HStack>

        <VStack w="100%" maxW="800px" alignItems="flex-start" spacing={4}>
          <Accordion
            defaultIndex={[0]}
            w="100%"
            allowToggle
            allowMultiple
            variant="section"
          >
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <b>Scan Settings</b>
                      </Box>
                      {isExpanded ? (
                        <ChevronUpIcon color="white" fontSize="xl" />
                      ) : (
                        <AccordionIcon />
                      )}
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <VStack w="100%" alignItems="flex-start" spacing={4}>
                      <ChakraReactSelectField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Member Field Lookup Order"
                        name="scanSettings.memberFieldLookupOrder"
                        options={memberLookupMethodOptions}
                        selectedOptionColor="blue"
                        isMulti
                        keepSelectionOrderInOptions={true}
                      />
                      <SwitchField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Validate Using Regex"
                        name="scanSettings.validateUsingRegex"
                      />
                      <TextField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Scan Data Regex"
                        name="scanSettings.scanDataRegex"
                        isDisabled={!values.scanSettings.validateUsingRegex}
                      />
                      <SwitchField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Scan Data Regex Keeps Matches"
                        name="scanSettings.scanDataRegexKeepsMatches"
                        isDisabled={!values.scanSettings.validateUsingRegex}
                      />
                      <SwitchField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Allow Alpha Characters"
                        name="scanSettings.allowAlphaCharacters"
                      />
                      <SwitchField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Allow Digits"
                        name="scanSettings.allowDigits"
                      />
                      <SwitchField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="Allow Non-Alphanumeric Characters"
                        name="scanSettings.allowNonAlphanumericCharacters"
                      />
                    </VStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>
        </VStack>

        <HStack width="100%" justifyContent="start" spacing="3" pt="8">
          <Button
            colorScheme="cherryButton"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || !isValid}
            onClick={submitForm}
          >
            Save
          </Button>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
      </FormStack>
    </Box>
  );
};

export const EventsModuleConfigurationDetailForm = withFormik<
  EMLConfigurationDetailFormProps,
  EMLConfigurationDetailFormValues
>({
  mapPropsToValues: ({ configuration }) => {
    return {
      scanSettings: {
        memberFieldLookupOrder:
          configuration?.ScanSettings?.MemberFieldLookupOrder || [],
        validateUsingRegex:
          configuration?.ScanSettings?.ValidateUsingRegex ?? false,
        scanDataRegex: configuration?.ScanSettings?.ScanDataRegex ?? null,
        scanDataRegexKeepsMatches:
          configuration?.ScanSettings?.ScanDataRegexKeepsMatches ?? false,
        allowAlphaCharacters:
          configuration?.ScanSettings?.AllowAlphaCharacters ?? true,
        allowDigits: configuration?.ScanSettings?.AllowDigits ?? true,
        allowNonAlphanumericCharacters:
          configuration?.ScanSettings?.AllowNonAlphanumericCharacters ?? false,
      },
    };
  },

  handleSubmit: async (values, { props, setSubmitting }) => {
    const configurationUpdate: CherryHubAdminApi.EventsModuleConfigurationUpdate =
      {
        ScanSettings: {
          MemberFieldLookupOrder: values.scanSettings.memberFieldLookupOrder,
          ValidateUsingRegex: values.scanSettings.validateUsingRegex,
          ScanDataRegex: values.scanSettings.scanDataRegex,
          ScanDataRegexKeepsMatches:
            values.scanSettings.scanDataRegexKeepsMatches,
          AllowAlphaCharacters: values.scanSettings.allowAlphaCharacters,
          AllowDigits: values.scanSettings.allowDigits,
          AllowNonAlphanumericCharacters:
            values.scanSettings.allowNonAlphanumericCharacters,
        },
      };

    const result =
      await props.apiClient.createOrUpdateEventsModuleConfiguration(
        props.businessId,
        configurationUpdate
      );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }

    setSubmitting(false);
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  // validationSchema: Yup.object({
  //   scanSettings: Yup.object({
  //     scanDataRegex: Yup.string().nullable().when('validateUsingRegex', {
  //       is: true,
  //       then: Yup.string().required('Regex pattern is required when validation is enabled')
  //     }),
  //   }),
  // }),
})(InnerForm);
